/**
 * Service to track manually excluded highlight instances
 * When a user manually unhighlights a specific occurrence, we remember it
 * and don't auto-highlight it again (unless manually highlighted again)
 */

interface ExcludedInstance {
  blockId: string; // The block/editor ID where this exclusion exists
  text: string; // The exact text that was unhighlighted
  itemId: string; // The highlight item ID it matches
  position: number; // Position in the block (to handle multiple occurrences)
}

export class ManualHighlightExclusionsService {
  // Map of blockId -> Set of excluded instance keys
  private exclusions: Map<string, Set<string>> = new Map();
  private listeners: Set<() => void> = new Set();

  /**
   * Create a unique key for an exclusion
   */
  private createKey(text: string, itemId: string, position: number): string {
    return `${text}|${itemId}|${position}`;
  }

  /**
   * Add a manual exclusion (user unhighlighted this instance)
   * @param blockId - The block/editor ID
   * @param text - The exact text
   * @param itemId - The highlight item ID
   * @param position - Position in the block
   */
  addExclusion(blockId: string, text: string, itemId: string, position: number = 0): void {
    if (!this.exclusions.has(blockId)) {
      this.exclusions.set(blockId, new Set());
    }
    
    const key = this.createKey(text, itemId, position);
    this.exclusions.get(blockId)!.add(key);
    this.notifyListeners();
  }

  /**
   * Remove a manual exclusion (user manually highlighted this instance again)
   * @param blockId - The block/editor ID
   * @param text - The exact text
   * @param itemId - The highlight item ID
   * @param position - Position in the block
   */
  removeExclusion(blockId: string, text: string, itemId: string, position: number = 0): void {
    const blockExclusions = this.exclusions.get(blockId);
    if (!blockExclusions) return;
    
    const key = this.createKey(text, itemId, position);
    blockExclusions.delete(key);
    
    // Clean up empty sets
    if (blockExclusions.size === 0) {
      this.exclusions.delete(blockId);
    }
    
    this.notifyListeners();
  }

  /**
   * Check if a specific instance is excluded
   * @param blockId - The block/editor ID
   * @param text - The exact text
   * @param itemId - The highlight item ID
   * @param position - Position in the block
   * @returns true if excluded, false if should be highlighted
   */
  isExcluded(blockId: string, text: string, itemId: string, position: number = 0): boolean {
    const blockExclusions = this.exclusions.get(blockId);
    if (!blockExclusions) return false;
    
    const key = this.createKey(text, itemId, position);
    return blockExclusions.has(key);
  }

  /**
   * Clear all exclusions for a specific block
   * @param blockId - The block/editor ID
   */
  clearBlockExclusions(blockId: string): void {
    this.exclusions.delete(blockId);
    this.notifyListeners();
  }

  /**
   * Clear all exclusions
   */
  clearAllExclusions(): void {
    this.exclusions.clear();
    this.notifyListeners();
  }

  /**
   * Get all exclusions for a block
   * @param blockId - The block/editor ID
   * @returns Array of exclusion keys
   */
  getBlockExclusions(blockId: string): string[] {
    const blockExclusions = this.exclusions.get(blockId);
    return blockExclusions ? Array.from(blockExclusions) : [];
  }

  /**
   * Subscribe to exclusion changes
   * @param listener - Callback function
   * @returns Unsubscribe function
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  /**
   * Get statistics
   */
  getStatistics(): {
    totalBlocks: number;
    totalExclusions: number;
  } {
    let totalExclusions = 0;
    this.exclusions.forEach(set => {
      totalExclusions += set.size;
    });

    return {
      totalBlocks: this.exclusions.size,
      totalExclusions,
    };
  }
}

// Export singleton instance
export const manualHighlightExclusionsService = new ManualHighlightExclusionsService();

